// TypeScript type definitions for the Visual Novel system
// Based on the Tech Plan specification

import type { PedaCategoryKey } from '../systems/peda-categories';

export type { PedaCategoryKey };

export type ProfileTheme = 'default' | 'cyberpunk';

export interface ScenarioMetadata {
  title: string;
  thumbnail: string;
  version?: string;
  startScene: string;
  achievements: Achievement[];
  skills?: Skill[];              // Optional array of skills
  characters?: CharacterPortrait[];  // Optional array of character portraits
  chapters?: Chapter[];          // Optional array of chapters
  missions?: Mission[];          // Optional missions shown individually in the library
  profileTheme?: ProfileTheme;   // Optional profile modal theme (default: 'default')
  /** When true, scenario is omitted from GET /api/scenarios (selection grid and admin pickers). @deprecated use releaseToProd */
  hiddenFromSelection?: boolean;
  /** When true, scenario is visible in the library for all users with access. When false, only darwinx_admin sees it. */
  releaseToProd?: boolean;
  /** Variables initialisées au démarrage du scénario (ex. peda_shields, peda_shields_max). */
  initialVariables?: Record<string, number>;
  /** Valeurs de placeholders injectées par l'API (lecture seule côté client). */
  textPlaceholders?: Record<string, string>;
  /** Registre global des catégories de jeu de tri, réutilisables entre les scènes situationSortGame. */
  situationSortCategories?: SituationSortCategory[];
  /** Libellés d'alliance affichés sur les écrans de fin (clés "1", "2", "3"). */
  trustLabels?: TrustLabels;
  /** Catalogue du carnet pédagogique (fiches + annuaire). */
  inventory?: ScenarioInventory;
}

export type InventoryPersonStatus = 'suspect' | 'compromis' | 'confiance' | 'neutre';

export interface InventorySection {
  id: string;
  title: string;
}

export interface InventoryDirectoryPerson {
  id: string;
  name: string;
  status: InventoryPersonStatus;
  previous?: string;
  condition?: string;
  note?: string;
}

export interface InventoryDirectoryGroup {
  title: string;
  entries: InventoryDirectoryPerson[];
}

export interface InventoryEntry {
  id: string;
  section: string;
  name: string;
  condition?: string;
  title?: string;
  text?: string;
  thought?: string;
  where?: string;
  image?: string;
  form?: 'directory';
  groups?: InventoryDirectoryGroup[];
  serial?: RecordCardSerial;
  danger?: RecordCardDanger;
  /** Id de la scène recordCard (attaque ou arrestation). Si `image` est omis, tuile et panneau lisent cette scène. */
  card?: string;
}

export interface ScenarioInventory {
  sections: InventorySection[];
  entries: InventoryEntry[];
}

export type TrustLabels = Partial<Record<'1' | '2' | '3', string>>;

export interface ChoiceTrustEntry {
  characterId: string;
  points: number;
}

export interface Achievement {
  id: string;                    // Unique identifier (e.g., "key_found")
  name: string;                  // Display name (e.g., "Key Collector")
  description: string;           // Description (e.g., "Found the old key")
  condition: string;             // Condition string (e.g., "flag:hasKey")
  icon?: string;                 // Optional icon path
}

export interface Skill {
  id: string;                    // Unique identifier (e.g., "hacking")
  name: string;                  // Display name (e.g., "Hacking")
  description: string;           // Description (e.g., "Ability to hack computer systems")
  icon?: string;                 // Optional icon path
}

export interface CharacterPortrait {
  id: string;                    // Unique identifier (e.g., "colleague1")
  name: string;                  // Display name (e.g., "Collègue 1")
  defaultImage: string;          // Default portrait image path
  icon?: string;                 // Icon image for dialogue box (circular portrait)
  emotions?: Record<string, string>;  // emotion name -> image path
  confidence?: number;           // Confidence level (0-100) set at runtime
}

export interface Chapter {
  id: string;                    // Unique identifier (e.g., "chapter1")
  title: string;                 // Display title (e.g., "Chapitre 1: Phishing")
  startScene: string;            // Starting scene ID for this chapter
  thumbnail?: string;            // Optional thumbnail image
  /** When set, chapter is only shown in menus for this library mission */
  missionId?: string;
}

export interface Mission {
  id: string;
  title: string;
  /** @deprecated Derived from the first chapter of the mission. Kept for legacy scenario JSON only. */
  startScene?: string;
  thumbnail?: string;
  order: number;
  /** Mission id that must be completed before this one is unlocked in the library */
  requiresMission?: string;
  /** Nombre maximal de boucliers PEDA gagnables dans cette mission. */
  pedaShieldsMax?: number;
}

export interface AnimationPreset {
  type: 'fadeIn' | 'fadeOut' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown';
  duration: number;
  delay?: number;
}

export interface ClickAction {
  type: 'dialogue' | 'collectItem' | 'setFlag' | 'goto' | 'changeBackground' | 'showTextOverlay';
  payload: unknown;
  condition?: string;
  nextAction?: ClickAction;
}

export interface MailboxData {
  from: string;
  to?: string;
  subject: string;
  content: string;
  metadata?: Record<string, string>;
}

export interface MailboxActions {
  reply?:       { goto: string; actions?: CommandItem[] };
  archive?:     { goto: string; actions?: CommandItem[] };
  report?:      { goto: string; actions?: CommandItem[] };
  clickOnLink?: { goto: string; actions?: CommandItem[] };
}

export interface SceneElement {
  id: string;
  type: 'character' | 'object' | 'hotspot' | 'text' | 'mailbox';
  image?: string;
  position: {
    x: number;
    y: number;
    width?: number;
    height?: number;
  };
  animation?: AnimationPreset;
  clickAction?: ClickAction;
  /** Action triggered when the player chooses "Rien a signaler" in object inspect mode */
  dismissAction?: ClickAction;
  /** Message affiché quand le joueur signale l'objet (scènes observation simplifiées). */
  reportMessage?: string;
  /** Message affiché quand le joueur ignore l'objet (scènes observation simplifiées). */
  dismissMessage?: string;
  /** PEDA-SHIELDS — boucliers gagnés si le joueur signale correctement cet objet. */
  pedaShields?: number;
  /** Catégorie PEDA affichée lors de l'animation de gain de bouclier. */
  pedaCategory?: PedaCategoryKey;
  /**
   * Force un objet à être cliquable/inspectable même sans message (report/dismiss)
   * encore renseigné. Utile pour un objet fraîchement créé dans l'éditeur.
   */
  inspectable?: boolean;
  condition?: string;
  noCursorChange?: boolean;
  
  // Character-specific
  moods?: Record<string, string>;
  defaultMood?: string;
  
  // Text-specific
  text?: string;
  textStyle?: {
    fontSize?: string;
    fontFamily?: string;
    fontWeight?: string;
    color?: string;
    textAlign?: 'left' | 'center' | 'right';
    backgroundColor?: string;
    padding?: string;
    textShadow?: string;
  };
  
  // Mailbox-specific
  mailbox?: MailboxData;
  mailboxActions?: MailboxActions;
}

export interface DialogueFields {
  speaker?: string;
  text: string;
  emotion?: string;
  characterId?: string;
  placement?: 'gauche' | 'centre' | 'droite';
  isInternal?: boolean;
  /** @deprecated Sprites come from metadata.characters; use characterId + emotion + placement. */
  characterMood?: {
    elementId: string;
    mood: string;
  };
}

export interface CommandItem {
  type: 'command';
  command: 'setFlag' | 'setVar' | 'collectItem' | 'showElement' | 'hideElement' | 'animateElement' | 'unlockSkill' | 'unlockAchievement';
  target: string;
  value?: unknown;
}

export interface TextOverlayOption {
  text: string;
  condition?: string;
  actions?: CommandItem[];
  goto?: string;
  closeOverlay?: boolean;
}

export interface ConfidenceGainEntry {
  characterId: string;
  amount: number;
}

export interface ChoiceOption {
  text: string;
  condition?: string;
  actions?: CommandItem[];
  /** Branch content played before implicit convergence. */
  scenes?: Scene[];
  /** Explicit jump (game over checkpoint, menu, etc.). */
  goto?: string;
  pedaShields?: number;
  pedaCategory?: PedaCategoryKey;
  /** Points de confiance à ajouter pour un ou plusieurs personnages. */
  confidenceGains?: ConfidenceGainEntry[];
  /** Points d'alliance cumulés sur la campagne (affichés en fin de chapitre). */
  trust?: ChoiceTrustEntry;
  moodChanges?: Array<{
    elementId: string;
    mood: string;
  }>;
  /** Déclenchée uniquement via le bouton STOP (masquée du panneau de choix). */
  stopButton?: boolean;
}

export interface TextOverlaySection {
  text: string;
  condition?: string;
}

export interface TextOverlayStar {
  condition: string;
}

export interface ChapterEndPerfRow {
  label: string;
  text: string;
  variant?: 'default' | 'trust';
  condition?: string;
}

export interface ChapterEndData {
  eyebrow: string;
  heading: string;
  missionLabel?: string;
  missionIndex: number;
  missionTotal: number;
  /** Nombre maximal de boucliers gagnables dans ce chapitre (affiché sur l'écran de fin). */
  chapterShieldsMax?: number;
  /** Légende sous le compteur de boucliers du chapitre. */
  chapterShieldsCaption?: string;
  keyIdea: string;
  reflexes: string[];
  perfNote?: string;
  perfRows: ChapterEndPerfRow[];
  /** Libellé du bouton continuer (la scène suivante est implicite). */
  continueText?: string;
}

export type TextOverlayType = 'email' | 'console' | 'terminal' | 'letter' | 'cyber' | 'poster' | 'note' | 'document' | 'summary' | 'chapterEnd';

export interface PhoneMailPreview {
  from?: string;
  subject?: string;
  preview?: string;
  receivedAt?: string;
  unreadCount?: number;
}

export interface ChoiceFields {
  prompt?: string;
  options: ChoiceOption[];
  phoneMailPreview?: PhoneMailPreview;
}

export interface ConfidenceGainFields {
  characters: Array<{
    characterId: string;
    confidenceLevel: number;
  }>;
  introText?: string;
  continueText?: string;
}

export interface ChatMessage {
  sender: 'contact' | 'player';
  text: string;
  time?: string;                   // e.g. "08:11"
}

/** Reply option shown in SMS / call UI; can be nested for follow-up choices. */
export interface PhoneChatReplyOption {
  text: string;
  contactReply?: string;
  goto?: string;
  replyOptions?: PhoneChatReplyOption[];
}

export interface PhoneChatFields {
  contactName: string;
  contactAvatar?: string;
  messages: ChatMessage[];
  messageDelay?: number;
  typingDelay?: number;
  replyOptions?: PhoneChatReplyOption[];
}

export interface PhoneCallMessage {
  sender: 'contact' | 'player';
  text: string;
}

/** Reply option during an in-call dialogue (same shape as SMS replies). */
export type PhoneCallReplyOption = PhoneChatReplyOption;

export type PhoneCallStyle = 'inline_call' | 'phone';

export interface PhoneCallFields {
  contactName: string;
  contactAvatar?: string;
  /** Directory role shown on inline_call card (e.g. "La Directrice"). */
  contactRole?: string;
  messages: PhoneCallMessage[];
  messageDelay?: number;
  /** Visual presentation. Defaults to inline_call when omitted. */
  style?: PhoneCallStyle;
  incomingCall?: boolean;
  autoAnswer?: boolean;
  allowDecline?: boolean;
  ringDurationMs?: number;
  ringAfterDeclineMs?: number;
  replyOptions?: PhoneCallReplyOption[];
}

export type PhoneMailHighlightField = 'from' | 'content';

export interface PhoneMailHighlightItem {
  term: string;
  field: PhoneMailHighlightField;
}

/** One beat after a triage button: optional zoom, then one or more reaction lines. */
export interface PhoneMailTriageReactionStep {
  reactions: string[];
  highlightInEmail?: PhoneMailHighlightItem[];
}

export interface PhoneMailTriageAction {
  label: string;
  goto?: string;
  actions?: CommandItem[];
  /** Ordered beats: optional highlight then reactions. Preferred over flat reactions. */
  reactionSteps?: PhoneMailTriageReactionStep[];
  /** @deprecated Use reactionSteps. Kept for legacy scenarios. */
  reactions?: string[];
  /** @deprecated Use reactionSteps[].highlightInEmail. */
  highlightInEmail?: PhoneMailHighlightItem[];
  /** PEDA-SHIELDS: boucliers gagnés si le joueur choisit cette réponse (bonne réponse). */
  pedaShields?: number;
  /** Catégorie PEDA affichée lors de l'animation de gain de bouclier. */
  pedaCategory?: PedaCategoryKey;
}

export interface PhoneMailTriageMail {
  id: string;
  from: string;
  subject: string;
  content: string;
  preview?: string;
  receivedAt?: string;
  triageActions: PhoneMailTriageAction[];
}

export interface PhoneMailInboxItem {
  from: string;
  subject: string;
  preview?: string;
  receivedAt?: string;
  unread?: boolean;
}

export interface PhoneMailAttachment {
  name: string;
  size?: string;
}

export interface PhoneMailFields {
  from?: string;
  subject?: string;
  content?: string;
  preview?: string;
  receivedAt?: string;
  attachments?: PhoneMailAttachment[];
  attachmentsNote?: string;
  unreadCount?: number;
  continueLabel?: string;
  startInReading?: boolean;
  mailProgress?: string;
  inboxItems?: PhoneMailInboxItem[];
  triageActions?: PhoneMailTriageAction[];
  mails?: PhoneMailTriageMail[];
  pedaShieldsMax?: number;
}

export interface PasswordGameProfile {
  name: string;
  age: string;
  position: string;
  city: string;
  partner?: string;
  pet?: string;
  birthDate: string;
  socialMedia: string;
}

export interface PasswordGameRound {
  profile: PasswordGameProfile;
  password: string;
  expectedScore: number;
  feedback: string;
  frankIntro: string;
  frankQuestion: string;
}

export interface ScreenPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface PasswordGameFields {
  rounds: PasswordGameRound[];
  victoryReward: string;
  screenPosition?: ScreenPosition;
  gotoRestart?: string;
  gotoMenu?: string;
}

export interface DragDropPhrase {
  id: number;
  text: string;
  isCorrect: boolean;
  feedback: string;
}

export interface DragDropResultRule {
  minCorrect: number;
  maxCorrect: number;
  response: string;
  action: 'retry' | 'continue';
}

export interface DragDropGameFields {
  phrases: DragDropPhrase[];
  requiredSelections: number;
  resultRules: DragDropResultRule[];
  victoryReward: string;
  victorySubReward?: string;
  starsTotal: number;
  screenPosition?: ScreenPosition;
  speakerName?: string;
  speakerIcon?: string;
  backgroundImage?: string;
  gotoRestart?: string;
  gotoMenu?: string;
}

export interface SituationSortCategory {
  id: string;
  label: string;
}

export interface SituationSortSituation {
  id: string;
  /** Libellé court affiché sur la carte (ex. « Phishing SMS »). Sinon « Situation N ». */
  label?: string;
  text: string;
  expectedCategoryId: string;
  feedbackOk: string;
  feedbackKo: string;
  /** PEDA-SHIELDS — boucliers gagnés si bonne réponse. */
  pedaShields?: number;
  /** Sous-titre affiché dans l'animation de gain (ex. catégorie pédagogique). */
  pedaCategory?: PedaCategoryKey;
}

export interface SituationSortGameFields {
  categories: SituationSortCategory[];
  situations: SituationSortSituation[];
  instructionText?: string;
  speakerName?: string;
  speakerCharacterId?: string;
  requireAllCorrect?: boolean;
  screenPosition?: ScreenPosition;
  gotoRestart?: string;
  gotoMenu?: string;
}

export interface TimedOverlayFields {
  message: string;
  durationMs: number;
}

export interface GameOverStamp {
  text: string;
  sub?: string;
}

export interface GameOverFields {
  heading?: string;
  text: string;
  restartLabel?: string;
  stamp?: GameOverStamp;
  /** Commands exécutées au clic sur Recommencer (ex. reset de flags). */
  actions?: CommandItem[];
}

export interface RecordCardSerial {
  serie: string;
  n: number;
  total: number;
}

export interface RecordCardDanger {
  score: number;
  max?: number;
  word?: string;
}

export interface RecordCardKeyFigure {
  n: string;
  label: string;
}

export interface RecordCardReflex {
  text: string;
  actions?: string[];
}

export interface RecordCardCaseCost {
  n: string;
  label: string;
}

export interface RecordCardCase {
  name: string;
  when: string;
  title: string;
  steps: string[];
  cost?: RecordCardCaseCost[];
}

export interface RecordCardFields {
  recordKind?: 'arrestation' | 'attaque';
  title: string;
  subtitle?: string;
  summary?: string;
  characterId?: string;
  image?: string;
  matricule?: string;
  serial?: RecordCardSerial;
  danger?: RecordCardDanger;
  taken?: string[];
  saved?: string[];
  stamp?: GameOverStamp;
  continueText?: string;
  /** Sous-ligne sous le bouton Continuer (ex. « MISSION 5 DANS UN INSTANT »). */
  nextHint?: string;
  /** Auteur / note interne — non affiché en jeu. */
  note?: string;
  /** Carte attaque — numéro de mission (affiché « RENCONTRÉ · MISSION N »). */
  mission?: number;
  /** Carte attaque — bandeau bas de l'illustration, en capitales. */
  moment?: string;
  /** Carte attaque — ce qui l'a fait repérer. */
  symptoms?: string;
  /** Carte attaque — ce qu'elle visait. */
  target?: string;
  /** Carte attaque — chiffre du pied de carte, tiré de la mission jouée. */
  keyFigure?: RecordCardKeyFigure;
  /** Carte attaque — réflexe sur la carte + gestes dans le panneau. */
  reflex?: RecordCardReflex;
  /** Carte attaque — panneau « C'est déjà arrivé ». */
  case?: RecordCardCase;
}

export interface PhoneMailInteractFields {
  from?: string;
  subject?: string;
  preview?: string;
  content?: string;
  receivedAt?: string;
  unreadCount?: number;
  attachments?: PhoneMailAttachment[];
  attachmentsNote?: string;
  continueLabel?: string;
  /** Phase de départ du téléphone (menu ou boîte de réception). */
  startPhase?: 'menu' | 'inbox';
  /** Attendre que le joueur ouvre le mail avant d'avancer. */
  waitForOpen?: boolean;
}

export interface TextOverlayFields {
  /** Peau visuelle. Omis = `letter` (charte). `cyber` = ancien canal sécurisé. */
  overlayType?: TextOverlayType;
  title?: string;
  /** Overlay letter / cyber — nom affiché de l'expéditeur. */
  characterName?: string;
  /** Overlay letter / cyber — icône circulaire de l'expéditeur (chemin asset). */
  characterIcon?: string;
  /** Overlay letter / cyber — libellé du bandeau (ex. « Mission 1 »). */
  channelLabel?: string;
  content: string;
  stars?: TextOverlayStar[];
  reward?: string;
  sections?: TextOverlaySection[];
  options?: TextOverlayOption[];
  /** Libellé du bouton continuer quand `options` est vide — la suite est la scène suivante. */
  continueText?: string;
  metadata?: Record<string, string>;
  enableTyping?: boolean;
  /** @deprecated Utiliser le type de scène `chapterEnd` */
  chapterEnd?: ChapterEndData;
}

export interface ObservationFields {
  /** Texte affiché tant que la gate de sortie est fermée. */
  instruction?: string;
  /** Texte affiché au-dessus du bouton Continuer quand la gate est ouverte. */
  exitPrompt?: string;
  /** Toasts déclenchés quand toutes les anomalies ont été signalées. */
  completionDialogues?: string[];
  exitChoice?: { text: string; condition?: string };
}

/** Zone cliquable sur un écran interactif (portail, bouton dessiné, etc.). */
export interface ClickableScreenZone {
  id: string;
  label: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  goto: string;
  pedaShields?: number;
  pedaCategory?: PedaCategoryKey;
  confidenceGains?: ConfidenceGainEntry[];
  actions?: CommandItem[];
  condition?: string;
}

export interface ClickableScreenFields {
  /** Pensée du personnage affichée en bas d'écran. */
  instruction?: string;
  zones: ClickableScreenZone[];
}

export type StagePlacement = 'gauche' | 'centre' | 'droite';

/** Personnage visible sur le plateau (hérité ou figé sur une scène). */
export interface StageCharacter {
  characterId: string;
  placement?: StagePlacement;
  emotion?: string;
}

/** Common fields on every flat scene block. */
export interface SceneBlockBase {
  id: string;
  background?: string;
  /**
   * Video backgrounds only. `false` plays once and holds the last frame.
   * Absent or `true` loops (current default). Travels with `background` on the spine.
   */
  backgroundLoop?: boolean;
  elements?: SceneElement[];
  condition?: string;
  /** Jump after completing this block instead of implicit next. */
  goto?: string;
  /** Vide tous les personnages hérités à partir de cette scène. */
  clearStage?: boolean;
  /** Plateau figé pour cette scène (remplace l'héritage automatique). */
  stageCharacters?: StageCharacter[];
}

export interface DialogueScene extends SceneBlockBase, DialogueFields {
  type: 'dialogue';
  /** Commands executed when the player lands on this replica. The replica is still shown. */
  actions?: CommandItem[];
}

export interface CommandScene extends SceneBlockBase, Omit<CommandItem, 'type'> {
  type: 'command';
}

export interface ChoiceScene extends SceneBlockBase, ChoiceFields {
  type: 'choice';
}

export interface TextOverlayScene extends SceneBlockBase, TextOverlayFields {
  type: 'textOverlay';
}

export interface ChapterEndScene extends SceneBlockBase, ChapterEndData {
  type: 'chapterEnd';
  /** @deprecated Utiliser continueText — la suite est la scène suivante dans le flux. */
  options?: TextOverlayOption[];
}

export interface ConfidenceGainScene extends SceneBlockBase, ConfidenceGainFields {
  type: 'confidenceGain';
}

export interface PhoneChatScene extends SceneBlockBase, PhoneChatFields {
  type: 'phoneChat';
}

export interface PhoneCallScene extends SceneBlockBase, PhoneCallFields {
  type: 'phoneCall';
}

export interface PhoneMailScene extends SceneBlockBase, PhoneMailFields {
  type: 'phoneMail';
}

export interface PhoneMailInteractScene extends SceneBlockBase, PhoneMailInteractFields {
  type: 'phoneMailInteract';
}

export interface PasswordGameScene extends SceneBlockBase, PasswordGameFields {
  type: 'passwordGame';
}

export interface DragDropGameScene extends SceneBlockBase, DragDropGameFields {
  type: 'dragDropGame';
}

export interface SituationSortGameScene extends SceneBlockBase, SituationSortGameFields {
  type: 'situationSortGame';
}

export interface TimedOverlayScene extends SceneBlockBase, TimedOverlayFields {
  type: 'timedOverlay';
}

export interface ObservationScene extends SceneBlockBase, ObservationFields {
  type: 'observation';
}

export interface ClickableScreenScene extends SceneBlockBase, ClickableScreenFields {
  type: 'clickableScreen';
}

export interface GameOverScene extends SceneBlockBase, GameOverFields {
  type: 'gameOver';
}

export interface RecordCardScene extends SceneBlockBase, RecordCardFields {
  type: 'recordCard';
}

export type Scene =
  | DialogueScene
  | CommandScene
  | ChoiceScene
  | TextOverlayScene
  | ChapterEndScene
  | ConfidenceGainScene
  | PhoneChatScene
  | PhoneCallScene
  | PhoneMailScene
  | PhoneMailInteractScene
  | PasswordGameScene
  | DragDropGameScene
  | SituationSortGameScene
  | TimedOverlayScene
  | ObservationScene
  | ClickableScreenScene
  | GameOverScene
  | RecordCardScene;

/** Keep in sync with the `Scene` union — used at runtime for JSON that TypeScript cannot check. */
export const SCENE_TYPES = [
  'dialogue',
  'command',
  'choice',
  'textOverlay',
  'chapterEnd',
  'confidenceGain',
  'phoneChat',
  'phoneCall',
  'phoneMail',
  'phoneMailInteract',
  'passwordGame',
  'dragDropGame',
  'situationSortGame',
  'timedOverlay',
  'observation',
  'clickableScreen',
  'gameOver',
  'recordCard',
] as const;

export type SceneType = (typeof SCENE_TYPES)[number];

type MissingSceneTypes = Exclude<Scene['type'], SceneType>;
type ExtraSceneTypes = Exclude<SceneType, Scene['type']>;
type AssertSceneTypesCovered = MissingSceneTypes extends never ? true : MissingSceneTypes;
type AssertSceneTypesExact = ExtraSceneTypes extends never ? true : ExtraSceneTypes;
const _sceneTypesSync: AssertSceneTypesCovered & AssertSceneTypesExact = true;
void _sceneTypesSync;

export function isKnownSceneType(type: string | null | undefined): type is SceneType {
  return typeof type === 'string' && (SCENE_TYPES as readonly string[]).includes(type);
}

export function formatUnknownSceneTypeMessage(type: string | null | undefined): string {
  const raw = typeof type === 'string' ? type.trim() : '';
  if (!raw) return 'Type de scène inconnu.';
  return `Type « ${raw} » inconnu.`;
}

/** @deprecated v1 content item — use Scene union instead. */
export type ContentItem = Scene;

/** @deprecated alias */
export type DialogueItem = DialogueScene;
export type ChoiceItem = ChoiceScene;
export type TextOverlayItem = TextOverlayScene;
export type ChapterEndItem = ChapterEndScene;
export type ConfidenceGainItem = ConfidenceGainScene;
export type PhoneChatItem = PhoneChatScene;
export type PhoneCallItem = PhoneCallScene;
export type PhoneMailItem = PhoneMailScene;
export type PhoneMailInteractItem = PhoneMailInteractScene;
export type PasswordGameItem = PasswordGameScene;
export type DragDropGameItem = DragDropGameScene;
export type SituationSortGameItem = SituationSortGameScene;
export type TimedOverlayItem = TimedOverlayScene;
export type GameOverItem = GameOverScene;
export type RecordCardItem = RecordCardScene;

export interface Item {
  id: string;
  name: string;
  description: string;
  icon: string;
}

export interface Scenario {
  metadata: ScenarioMetadata;
  items: Item[];
  scenes: Scene[];
}

export interface GameState {
  scenarioId: string;
  /** Active mission when playing a scenario split into library missions */
  missionId?: string;
  currentSceneId: string;
  flags: Record<string, boolean>;
  variables: Record<string, number>;
  inventory: string[];
  visitedScenes: Set<string>;
  unlockedAchievements: string[];
  unlockedSkills: string[];      // Array of unlocked skill IDs
  hiddenElements: Set<string>;   // Set of hidden element IDs
  isAchievementAnimating?: boolean;
  lastUnlockedAchievementId?: string;
  /** True when the player has finished the scenario; used to show end screen on re-entry */
  scenarioCompleted?: boolean;
}

export interface SaveData {
  scenarioId: string;
  timestamp: number;
  gameState: Omit<GameState, 'visitedScenes' | 'hiddenElements'> & {
    visitedScenes: string[];
    hiddenElements: string[];
  };
}

// Export validation types and functions
export * from './validation';
export * from './review';
